import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { Seo, MarketingShell, PageHero, Band } from '@/seo/kit';

const BORDER = '#D8DEE8';
const SUBTLE = '#5B6678';
const GREEN = '#1F7A4F';

const TIERS = [
  {
    id: 'basic', name: 'Basic', monthlyPrice: 39, annualPrice: 234,
    description: 'For solo builders who want structure without complexity.',
    features: [
      { name: '1 active project', included: true },
      { name: 'Single user (1 seat)', included: true },
      { name: 'Unlimited draw requests', included: true },
      { name: 'Standard draw checklists', included: true },
      { name: 'PDF exports', included: true },
      { name: 'Email support', included: true },
      { name: '10 GB document storage', included: true },
      { name: 'Add-ons available à la carte ($19/mo)', included: true },
      { name: 'Multiple projects (upgrade to Pro)', included: false },
      { name: 'AI draw readiness & add-ons included', included: false },
    ],
  },
  {
    id: 'pro', name: 'Pro', monthlyPrice: 69, annualPrice: 414,
    description: 'For builders and developers managing multiple projects.',
    features: [
      { name: 'Up to 2 active projects', included: true },
      { name: 'Single user (1 seat)', included: true },
      { name: 'Add-ons à la carte ($19/mo each)', included: true },
      { name: 'AI draw readiness & missing-doc detection', included: true },
      { name: 'Smart phase tracking', included: true },
      { name: 'Contractor Payment Report (export for accounting)', included: true },
      { name: 'Lender-ready & branded exports', included: true },
      { name: 'Priority support', included: true },
      { name: '50 GB document storage', included: true },
    ],
  },
  {
    id: 'advanced', name: 'Advanced', monthlyPrice: 129, annualPrice: 1290,
    description: 'For growing builders running several projects at once.',
    features: [
      { name: 'Up to 4 active projects', included: true },
      { name: 'Up to 2 seats', included: true },
      { name: 'Everything in Pro', included: true },
      { name: 'Add-ons à la carte ($19/mo each)', included: true },
      { name: 'AI draw readiness & missing-doc detection', included: true },
      { name: 'Contractor Payment Report', included: true },
      { name: 'Priority support', included: true },
      { name: '100 GB document storage', included: true },
    ],
  },
  {
    id: 'enterprise', name: 'Enterprise', monthlyPrice: 199, annualPrice: 1194,
    description: 'For large builders and development teams that need scale and control.',
    features: [
      { name: 'Unlimited projects', included: true },
      { name: 'Up to 4 seats', included: true },
      { name: 'Everything in Advanced', included: true },
      { name: 'All 4 add-ons included (no à la carte fees)', included: true },
      { name: 'Roles & permissions', included: true },
      { name: 'Audit logs & activity history', included: true },
      { name: 'White-labeled draw exports', included: true },
      { name: 'Dedicated onboarding', included: true },
      { name: '200 GB+ document storage', included: true },
      { name: 'Priority support', included: true },
    ],
    contactUs: true,
  },
];

function Pricing({ user }) {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(null);

  const handleSelectPlan = async (tier) => {
    if (!user) { navigate('/signup'); return; }
    if (tier.contactUs) { window.location.href = '/contact'; return; }
    setLoading(tier.id);
    try {
      const response = await apiService.createCheckoutSession({
        tier_id: tier.id,
        billing_cycle: isAnnual ? 'annual' : 'monthly',
        user_id: user.id,
        origin_url: window.location.origin,
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const getDisplayPrice = (tier) => (isAnnual ? Math.round(tier.annualPrice / 12) : tier.monthlyPrice);

  return (
    <MarketingShell>
      <Seo title="Pricing | DrawReady" description="Simple, transparent pricing for construction draw management. All plans include a 30-day free trial." path="/pricing" />

      <PageHero title="Simple, transparent pricing." bg="#EEF2F8">
        Choose the plan that fits your business. All plans include a 30-day free trial.
      </PageHero>

      <Band bg="#fff">
        <div className="flex items-center gap-3">
          <button onClick={() => setIsAnnual(false)} className={!isAnnual ? 'btn-primary' : 'btn-secondary'}>Monthly</button>
          <button onClick={() => setIsAnnual(true)} className={isAnnual ? 'btn-primary' : 'btn-secondary'}>Annual</button>
          {isAnnual && <span className="text-sm font-semibold" style={{ color: GREEN }}>Save 50% — first year only</span>}
        </div>

        <div className="grid md:grid-cols-4 mt-10 border-t border-b" style={{ borderColor: BORDER }}>
          {TIERS.map((tier, i) => (
            <div key={tier.id} className={`py-8 md:px-6 ${i === 0 ? 'md:pl-0' : 'md:border-l'}`} style={{ borderColor: BORDER }} data-testid={`plan-${tier.id}`}>
              <h2 className="text-2xl">{tier.name}</h2>
              <p className="text-sm mt-2 min-h-[48px]" style={{ color: SUBTLE }}>{tier.description}</p>
              <div className="mt-4">
                <span className="text-4xl font-bold" style={{ color: '#13294B' }}>${getDisplayPrice(tier)}</span>
                <span style={{ color: SUBTLE }}> /mo</span>
                {isAnnual && <p className="text-xs mt-1" style={{ color: GREEN }}>${tier.annualPrice} billed annually</p>}
              </div>
              <button
                onClick={() => handleSelectPlan(tier)}
                disabled={loading === tier.id}
                className="btn-primary w-full mt-5 text-[15px]"
                data-testid={`plan-${tier.id}-cta`}
              >
                {loading === tier.id ? 'Processing…' : tier.contactUs ? 'Contact us' : 'Start your 30-day free trial'}
              </button>
              <ul className="mt-6 space-y-2.5 text-[15px]">
                {tier.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-2" style={{ color: f.included ? '#1D2939' : '#98A2B3' }}>
                    <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: f.included ? '#13294B' : '#CBD2DD' }} />
                    <span style={f.included ? {} : { textDecoration: 'line-through' }}>{f.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Band>

      <Band bg="#F2F4F7">
        <h3 className="text-2xl mb-5">Included in every plan</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3">
          {['Secure cloud storage', 'Unlimited draw submissions', 'Lender-ready documentation', 'Ongoing platform updates'].map((f) => (
            <div key={f} className="flex items-center gap-2" style={{ color: '#1D2939' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#13294B' }} />
              <span className="text-[15px]">{f}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-lg border p-6 max-w-2xl bg-white" style={{ borderColor: BORDER }}>
          <h4 className="text-lg">First-year discount</h4>
          <p className="mt-1" style={{ color: SUBTLE }}>Pay upfront for the full year and get <span style={{ color: GREEN, fontWeight: 700 }}>50% off</span> your first year. This promotional rate applies to all new users.</p>
        </div>

        <p className="mt-10 text-[15px]" style={{ color: SUBTLE }}>Questions? <Link to="/contact" className="font-medium" style={{ color: '#1E4ED8' }}>Contact us</Link>.</p>
      </Band>
    </MarketingShell>
  );
}

export default Pricing;
